import { translate } from "@/lib/translation";
import { API_BASE_URL, authorizedFetch } from "./base";
import { consumeAppBootFlag } from "@/lib/appBoot";
import {
  UserSettings,
  UserSettingsUpdate,
  Country,
  Fiat,
  CurrencyOption,
  ExchangeRateResponse,
  TickerNotification,
} from "./types";

export async function getUserSettings(): Promise<UserSettings> {
  // X-App-Boot marks the first fetch after a real page load, which is what the
  // backend counts as a daily-active signal. See lib/appBoot.ts.
  // NOTE: no parameters. This is passed straight to TanStack Query as a
  // queryFn, which would hand its own context object to any argument here.
  const headers = consumeAppBootFlag() ? { "X-App-Boot": "1" } : undefined;
  return authorizedFetch<UserSettings>("/users/me/settings", { headers });
}

/**
 * Sends the daily-active signal unconditionally, for the case the page-load
 * flag cannot cover: a tab left open across midnight. See lib/appBoot.ts.
 * The caller must have claimed the day first via claimActivityMark().
 */
export async function markUserActive(): Promise<void> {
  await authorizedFetch<UserSettings>("/users/me/settings", {
    headers: { "X-App-Boot": "1" },
  });
}

export async function getTickerNotifications(): Promise<TickerNotification[]> {
  return authorizedFetch<TickerNotification[]>(
    "/users/me/ticker-notifications",
  );
}

export async function ackTickerNotifications(ids: number[]): Promise<void> {
  await authorizedFetch<void>("/users/me/ticker-notifications/ack", {
    method: "POST",
    body: JSON.stringify({ ids }),
  });
}

export async function updateUserSettings(
  settings: UserSettingsUpdate,
): Promise<UserSettings> {
  return authorizedFetch<UserSettings>("/users/me/settings", {
    method: "PUT",
    body: JSON.stringify(settings),
  });
}

export async function getCountries(): Promise<Country[]> {
  return authorizedFetch<Country[]>("/countries/");
}

export async function getCountry(code: string): Promise<Country> {
  return authorizedFetch<Country>(`/countries/${encodeURIComponent(code)}`);
}

export async function getCurrencies(): Promise<CurrencyOption[]> {
  return authorizedFetch<CurrencyOption[]>("/currencies/");
}

export async function getLatestExchangeRate(
  from: Fiat,
  to: Fiat,
): Promise<number | null> {
  const today = new Date().toISOString().slice(0, 10);
  try {
    const response = await authorizedFetch<ExchangeRateResponse>(
      `/exchange-rates/historical?date=${today}&from_curr=${from}&to_curr=${to}`,
    );
    return typeof response.rate === "number" ? response.rate : null;
  } catch (error) {
    console.error("Failed to fetch latest exchange rate", { error, from, to });
    return null;
  }
}

export async function exportUserData(): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/users/me/export`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error(translate("settings:toast.exportFailed"));
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "oitava-export.json";
  a.click();
  URL.revokeObjectURL(url);
}

export async function deleteAccount(): Promise<void> {
  await authorizedFetch<void>("/users/me", { method: "DELETE" });
}

export async function getHistoricalExchangeRate(
  date: string,
  fromCurr: string,
  toCurr: string,
): Promise<number> {
  if (!fromCurr || !toCurr) {
    throw new Error(
      "fromCurr and toCurr are required for getHistoricalExchangeRate",
    );
  }
  const response = await authorizedFetch<{ rate: number }>(
    `/exchange-rates/historical?date=${date}&from_curr=${fromCurr}&to_curr=${toCurr}`,
  );
  return response.rate;
}
